import { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Center, Stack, Text } from '@mantine/core';

import Button from '@/components/content/button/Button';
import BackLink from '@/components/content/link/BackLink';
import RouterLink from '@/components/content/link/RouterLink';
import MercurySkeleton from '@/components/content/placeholder/Skeleton';
import { MercuryCol, MercuryGrid } from '@/components/grid/Grid';

import { useActiveClientId, useMonitoringClients } from '../hooks/useActiveClientId';
import { useMonitoringCompany } from '../services/monitoringOverview';
import { monitoringPaths } from '../utils/monitoringPaths';

/**
 * Guards `/monitoring/:client/supplier/:supplier_id` (and nested incident /
 * risk-assessment pages). Deep links to a company that is not in the active
 * client's supplier list show an explicit not-found instead of a confusing
 * cross-tenant supplier page.
 */
export const MonitoringSupplierLayout = () => {
  const params = useParams();
  const supplierId = Number(params.supplier_id);
  const { clientId } = useActiveClientId();
  const { nameOf } = useMonitoringClients();
  const companyQuery = useMonitoringCompany(clientId);

  const belongs = useMemo(() => {
    if (!Number.isFinite(supplierId) || !companyQuery.isSuccess) return false;
    const suppliers = companyQuery.data?.suppliers;
    if (!Array.isArray(suppliers)) return false;
    return suppliers.some((supplier) => supplier.id === supplierId);
  }, [companyQuery.isSuccess, companyQuery.data, supplierId]);

  const backTo = clientId != null ? monitoringPaths.overview(clientId) : monitoringPaths.base;
  const backLink = <BackLink to={backTo}>Back to Supply Chain Monitoring</BackLink>;
  const clientLabel = nameOf(clientId) || 'this organization';

  if (companyQuery.isError) {
    return (
      <MercuryGrid>
        <MercuryCol transparent>{backLink}</MercuryCol>
        <MercuryCol transparent>
          <Center p={25}>
            <Text size="md" c="var(--quo-power-50)">
              {companyQuery.error?.message ?? 'Failed to verify supplier membership.'}
            </Text>
          </Center>
        </MercuryCol>
      </MercuryGrid>
    );
  }

  if (companyQuery.isPending) {
    return (
      <MercuryGrid>
        <MercuryCol transparent>{backLink}</MercuryCol>
        <MercuryCol transparent>
          <MercurySkeleton n={1} id="supplier-membership-skeleton" height={240} />
        </MercuryCol>
      </MercuryGrid>
    );
  }

  if (!belongs) {
    return (
      <MercuryGrid>
        <MercuryCol transparent>{backLink}</MercuryCol>
        <MercuryCol transparent>
          <Center p={25}>
            <Stack gap="md" align="center" maw={480}>
              <Text size="md" ta="center">
                This supplier is not in {clientLabel}&apos;s supply chain.
              </Text>
              {clientId != null && (
                <Button
                  component={RouterLink}
                  to={monitoringPaths.overview(clientId)}
                  variant="outline"
                >
                  Back to overview
                </Button>
              )}
            </Stack>
          </Center>
        </MercuryCol>
      </MercuryGrid>
    );
  }

  return <Outlet />;
};
